import React, { MouseEvent } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCheckCircle,
  faChevronLeft,
  faChevronRight,
  faInfoCircle,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Icon, IconVariant } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { ClampedContent } from "src/design-system/utils/ClampedContent";

export type CalloutVariant =
  | "error"
  | "warning"
  | "info"
  | "success"
  | "neutral";

type VariantStyle = {
  border: `border-${string}-${number}`;
  container: string;
  icon: IconProp;
  iconVariant: IconVariant;
  clamp: {
    color: `text-${string}-${number}`;
    fadeToColor: `to-${string}`;
    bgColor: `bg-${string}`;
  };
};

const VARIANT_STYLES: Record<CalloutVariant, VariantStyle> = {
  error: {
    border: "border-red-200",
    container: "bg-red-50 text-red-600",
    icon: faWarning,
    iconVariant: "error",
    clamp: {
      color: "text-red-600",
      fadeToColor: "to-red-50",
      bgColor: "bg-red-50",
    },
  },
  warning: {
    border: "border-orange-200",
    container: "bg-orange-50 text-orange-600",
    icon: faWarning,
    iconVariant: "warning",
    clamp: {
      color: "text-orange-600",
      fadeToColor: "to-orange-50",
      bgColor: "bg-orange-50",
    },
  },
  info: {
    border: "border-indigo-200",
    container: "bg-indigo-50 text-indigo-600",
    icon: faInfoCircle,
    iconVariant: "active",
    clamp: {
      color: "text-indigo-600",
      fadeToColor: "to-indigo-50",
      bgColor: "bg-indigo-50",
    },
  },
  success: {
    border: "border-green-200",
    container: "bg-green-50 text-green-600",
    icon: faCheckCircle,
    iconVariant: "success",
    clamp: {
      color: "text-green-600",
      fadeToColor: "to-green-50",
      bgColor: "bg-green-50",
    },
  },
  neutral: {
    border: "border-gray-200",
    container: "bg-gray-50 text-gray-600",
    icon: faInfoCircle,
    iconVariant: "default",
    clamp: {
      color: "text-gray-600",
      fadeToColor: "to-gray-50",
      bgColor: "bg-gray-50",
    },
  },
};

type ActionObject = {
  text: string;
  onClick: () => void;
};

type BaseCalloutProps = {
  children: React.ReactNode;
  action?: ActionObject | React.ReactNode;
  isEdgeToEdge?: boolean;
  dataLoc?: string;
};

type CalloutProps = BaseCalloutProps &
  (
    | { variant: "neutral"; icon?: IconProp; isIconSpinning?: boolean }
    | {
        variant: Exclude<CalloutVariant, "neutral">;
        icon?: never;
        isIconSpinning?: never;
      }
  );

const isActionObject = (
  action: NonNullable<CalloutProps["action"]>,
): action is ActionObject =>
  typeof action === "object" && action !== null && "onClick" in action;

const ACTION_CLASSES =
  "ml-auto flex-shrink-0 whitespace-nowrap font-inter-medium-12px underline";

const ActionContent: React.FC<{
  action: NonNullable<CalloutProps["action"]>;
}> = ({ action }) =>
  isActionObject(action) ? (
    <button className={ACTION_CLASSES} type="button" onClick={action.onClick}>
      {action.text}
    </button>
  ) : (
    <div className={ACTION_CLASSES}>{action}</div>
  );

type CalloutComponent = React.FC<CalloutProps> & {
  BlobContent: React.FC<CalloutBlobContentProps>;
};

export const Callout: CalloutComponent = ({
  variant,
  children,
  action,
  isEdgeToEdge = false,
  icon,
  dataLoc,
  isIconSpinning,
}) => {
  const styles = VARIANT_STYLES[variant];

  return (
    <div
      className={twJoin(
        "flex w-full items-start gap-x-1 rounded-lg border py-2 pr-4 pl-3 font-inter-normal-12px",
        styles.border,
        styles.container,
        isEdgeToEdge && "rounded-none border-x-0",
      )}
      data-loc={dataLoc ?? `info-pill-${variant}`}
      role={variant === "error" || variant === "warning" ? "alert" : "status"}
    >
      <Icon
        icon={icon ?? styles.icon}
        size="xs"
        spin={isIconSpinning}
        variant={styles.iconVariant}
      />

      <ClampedContent lines={3} {...styles.clamp}>
        {children}
      </ClampedContent>

      {action && <ActionContent action={action} />}
    </div>
  );
};

export type CalloutBlobVariant = "error" | "warning";

export type CalloutBlobItem = {
  title: string;
  errorBlob: string;
  isCodeBlock: boolean;
  dataLoc?: string;
};

type CalloutBlobContentProps = {
  items: CalloutBlobItem[];
  dataLoc?: string;
  variant?: CalloutBlobVariant;
};

const BLOB_VARIANT_CLASSES = {
  error: "border-red-200 bg-red-50",
  warning: "border-orange-200 bg-orange-50",
} as const;

const CalloutBlobItemContent: React.FC<
  CalloutBlobItem & { variant: CalloutBlobVariant }
> = ({ title, errorBlob, isCodeBlock, dataLoc, variant }) => {
  if (!isCodeBlock) {
    return (
      <div data-loc={dataLoc}>
        {title} {errorBlob}
      </div>
    );
  }

  return (
    <div data-loc={dataLoc} className="flex flex-col gap-y-2">
      <div>{title}</div>
      <div
        className={twJoin(
          "relative rounded-lg border p-3 font-code-12",
          BLOB_VARIANT_CLASSES[variant],
        )}
      >
        <div className="whitespace-pre-wrap pr-8">{errorBlob}</div>
        <div className="absolute top-3 right-3">
          <CopyTextIcon
            size="xs"
            tooltip="Copy"
            value={errorBlob}
            variant={variant}
          />
        </div>
      </div>
    </div>
  );
};

const CalloutBlobContent: React.FC<CalloutBlobContentProps> = ({
  items,
  dataLoc,
  variant = "error",
}) => (
  <div className="flex flex-col gap-y-2" data-loc={dataLoc}>
    {items.map((item) => (
      <CalloutBlobItemContent
        key={item.title}
        dataLoc={item.dataLoc}
        errorBlob={item.errorBlob}
        isCodeBlock={item.isCodeBlock}
        title={item.title}
        variant={variant}
      />
    ))}
  </div>
);

Callout.BlobContent = CalloutBlobContent;

const OVERLAY_SHADOW_CLASSES = {
  error: "shadow-red-50",
  warning: "shadow-orange-50",
} as const;

type MultipleErrorCalloutProps = {
  onClickPrev: (e: MouseEvent) => void;
  onClickNext: (e: MouseEvent) => void;
  displayedIndex: number;
  totalErrorsCount: number;
  errorSections: CalloutBlobItem[];
  failureCount: number | undefined;
  outdatedTooltipTitle?: string;
  action?: {
    text: string;
    onClick: () => void;
  };
  variant: "error" | "warning";
  dataLoc?: string;
};

export const MultipleErrorCallout: React.FC<MultipleErrorCalloutProps> = ({
  onClickPrev,
  onClickNext,
  displayedIndex,
  totalErrorsCount,
  errorSections,
  failureCount,
  action,
  outdatedTooltipTitle = "Test run again to update data",
  variant = "error",
  dataLoc = "error-selector",
}) => {
  const styles = VARIANT_STYLES[variant];
  return (
    <div
      className={twJoin(
        "flex max-h-56 w-full items-stretch gap-x-3 rounded-lg border pl-3 font-inter-normal-12px",
        styles.border,
        styles.container,
      )}
      data-loc={dataLoc}
      role="alert"
    >
      <Tooltip
        disabled={variant !== "warning"}
        placement="top"
        title={outdatedTooltipTitle}
        asChild
      >
        <div className="flex items-center self-start py-2">
          <button
            type="button"
            aria-label="Previous error"
            onClick={onClickPrev}
          >
            <Icon icon={faChevronLeft} size="xs" variant={variant} />
          </button>

          <Icon icon={faWarning} size="xs" variant={variant} />

          <span className="ml-0.5">
            {`${
              displayedIndex > -1 ? displayedIndex + 1 : "?"
            } of ${totalErrorsCount}${variant === "warning" ? " outdated" : ""} errors`}
          </span>
          <button type="button" aria-label="Next error" onClick={onClickNext}>
            <Icon icon={faChevronRight} size="xs" variant={variant} />
          </button>
        </div>
      </Tooltip>
      <div className={twJoin("border-l", styles.border)} />
      <div className="relative min-w-0 flex-1 py-2">
        <div className="error-selector-scrollbar compensate-scrollbar-gutter max-h-full min-w-0 overflow-auto break-words">
          <Callout.BlobContent items={errorSections} variant={variant} />
        </div>
        {action ? (
          <button
            type="button"
            className={twJoin(
              "absolute top-2 right-5 whitespace-nowrap font-inter-medium-12px underline shadow-lg",
              styles.clamp.bgColor,
              OVERLAY_SHADOW_CLASSES[variant],
            )}
            onClick={action.onClick}
          >
            {action.text}
          </button>
        ) : (
          failureCount !== undefined && (
            <span
              className={twJoin(
                "absolute top-2 right-5 whitespace-nowrap shadow-lg",
                styles.clamp.bgColor,
                OVERLAY_SHADOW_CLASSES[variant],
              )}
            >
              {failureCount} failures
            </span>
          )
        )}
      </div>
    </div>
  );
};
